import { MoveMultipleObjectsCommand } from "@/commands/MoveMultipleObjectsCommand.js"
import { MoveObjectCommand } from "@/commands/MoveObjectCommand.js"
import { UpdateConnectionRouteCommand } from "@/commands/UpdateConnectionRouteCommand.js"
import { CANVAS_SETTINGS_KEY, LINE_TYPES } from "@/data/constants"
import { _sleep } from "@/helpers/misc"
import { isStoredCoordinate } from "@/helpers/item-position.js"
import { useStorage } from 'nvd-use-storage'
import { CanvasSelectedItems } from "@/modules/canvas/CanvasSelectedItems.js"
import {
    assignConnectionLanes,
    cloneConnectionRoute,
    normalizeConnectionRoute,
} from "@/modules/canvas/connection-routing.js"
import { canEditConnectionRoutes as connectionRoutesAreEditable } from "@/modules/canvas/connection-edit-access.js"
import { Mouse } from "@/modules/canvas/Mouse.js"
import { canvasFont } from "@/modules/canvas/styles.js"
import { Connection } from "@/modules/canvas/table/Connection.js"
import { Note } from "@/modules/canvas/table/Note.js"
import { Table } from "@/modules/canvas/table/Table.js"
import {
    calculateViewportCenter,
    calculateViewportFit,
} from "@/modules/canvas/viewport-fit.js"
import { RelationshipResolver } from "@/modules/canvas/RelationshipResolver.js"
import { Easing, Tween } from "@tweenjs/tween.js"
import { Storage } from "nvd-js-helpers/storage-helper";
import { cssVar } from "../../helpers/misc";
import { useSettingsStore } from "../../stores/settings.store";

export class ApmCanvas {
    element
    ctx
    width = 800
    height = 600
    origin = {x: 0, y: 0}
    lastOrigin = {x: 0, y: 0}
    mouse
    draggedObject = null
    hoveredObject = null
    hoveredItem = null
    selectedObject = null
    highlightedItem = null
    editedNote = null
    editedTable = null
    editedField = null
    selectedLogicalTable = null
    selectedConnectionKey = null
    selectedConnectionField = null
    selectedConnection = null
    selectedConnectionPosition = null
    hoveredConnectionField = null
    hoveredConnectionKey = null
    renderedConnections = []
    connectionInteraction = null
    minX = 0
    minY = 0
    maxX = 1600
    maxY = 1200
    zoom = {scale: 1, delta: 0.2, min: 0.06, max: 2.5}
    selectedItems
    tab
    settings = {}
    exporting = false

    constructor(tab) {
        this.tab = tab
        this.mouse = new Mouse()
        this.selectedItems = new CanvasSelectedItems()
        this.settings = useStorage(CANVAS_SETTINGS_KEY)
    }

    calculateCenter() {
        const x = (-this.origin.x + this.width / 2) / this.zoom.scale
        const y = (-this.origin.y + this.height / 2) / this.zoom.scale
        return {x, y}
    }

    addNewItem(item) {
        let {x, y} = this.calculateCenter()
        if (!isStoredCoordinate(item.left)) item.left = x - 100
        if (!isStoredCoordinate(item.top)) item.top = y - 100
    }

    setEditedNote(note) {
        this.editedNote = note
    }

    setEditedTable(table) {
        this.editedTable = table
    }

    setEditedField(field) {
        this.editedField = field
    }

    updateSettings(key, value) {
        this.settings[key] = value
        Storage.setObject(CANVAS_SETTINGS_KEY, this.settings)
    }

    setSelectedObject(object) {
        if (!!object && !!this.selectedLogicalTable) {
            return this.setSelectedObject(null)
        }
        this.selectedObject = object
    }

    setDraggedObject(object) {
        if (object) this.setSelectedConnection(null)
        this.draggedObject = object
        this.onDrag(object)
    }

    setHoveredObject(object) {
        this.hoveredObject = object
        this.onHover(object)
        if (!object) return this.setHoveredItem(null)
        this.setHoveredItem(object)
    }

    setHoveredItem(object) {
        this.hoveredItem = object ? object.fields ? new Table(this, object) : new Note(this, object) : null
    }

    setSelectedLogicalTable(object) {
        this.selectedLogicalTable = object
        if (!!object) this.setSelectedObject(null)
    }

    screenToWorld(x, y) {
        return {
            x: (x - this.origin.x) / this.zoom.scale,
            y: (y - this.origin.y) / this.zoom.scale,
        }
    }

    async canEditConnectionRoutes() {
        const [
            { useTabsStore },
            { useVersionsStore },
            { useMenuStore },
        ] = await Promise.all([
            import('../../stores/tabs.store'),
            import('../../stores/versions.store'),
            import('../../stores/menu.store'),
        ])
        const tabs = useTabsStore()
        const versions = useVersionsStore()
        const menu = useMenuStore()

        return connectionRoutesAreEditable({
            schemaReadonly: tabs.isSchemaReadonly,
            templateReadonly: tabs.isTemplateReadonly,
            isPreviewing: versions.isPreviewing,
            isImporting: this.tab?.isImporting,
            embedded: menu.embedded,
        })
    }

    sourceTableForField(field) {
        return this.tab?.schema?.schema_data?.tables?.find(table =>
            table.fields?.includes(field)
            || table.fields?.some(candidate =>
                field?.p_id && field.p_id !== 'new' && candidate.p_id === field.p_id
            )
            || table.fields?.some(candidate =>
                candidate.name === field?.name && candidate.order === field?.order
            )
        ) || null
    }

    connectionKeyForField(field, table = null) {
        const sourceTable = table || this.sourceTableForField(field)
        const tableKey = sourceTable?.p_id && sourceTable.p_id !== 'new'
            ? sourceTable.p_id
            : sourceTable?.name
        const fieldKey = field?.p_id && field.p_id !== 'new'
            ? field.p_id
            : `${field?.name || ''}:${field?.order || field?.idx || ''}`
        return tableKey === undefined || tableKey === null
            ? null
            : JSON.stringify([String(tableKey), String(fieldKey)])
    }

    connectionKey(connection) {
        return this.connectionKeyForField(
            connection?.fromRow?.data,
            connection?.fromRow?.table?.data
        )
    }

    isConnectionSelected(connection) {
        return !!this.selectedConnectionKey
            && this.selectedConnectionKey === this.connectionKey(connection)
    }

    isConnectionHovered(connection) {
        return !!this.hoveredConnectionKey
            && this.hoveredConnectionKey === this.connectionKey(connection)
    }

    connectionAtScreen(x, y) {
        if (this.hoveredObject) return null
        const point = this.screenToWorld(x, y)
        const tolerance = 9 / this.zoom.scale
        let closest = null
        let closestDistance = tolerance

        for (const connection of this.renderedConnections) {
            const distance = connection.distanceTo(point)
            if (distance <= closestDistance) {
                closest = connection
                closestDistance = distance
            }
        }

        return closest
    }

    setSelectedConnection(connection) {
        this.selectedConnectionKey = connection ? this.connectionKey(connection) : null
        this.selectedConnectionField = connection?.fromRow?.data || null
        this.selectedConnection = connection || null
        if (connection) {
            this.setSelectedObject(null)
            this.selectedItems.clear()
        } else {
            this.selectedConnectionPosition = null
        }
    }

    updateConnectionInteractionState(connections) {
        if (this.exporting) return

        const hoveredConnection = this.connectionAtScreen(this.mouse.x, this.mouse.y)
        this.hoveredConnectionKey = hoveredConnection ? this.connectionKey(hoveredConnection) : null
        this.hoveredConnectionField = hoveredConnection?.fromRow?.data || null

        if (this.selectedConnectionKey) {
            this.selectedConnection = connections.find(connection =>
                this.connectionKey(connection) === this.selectedConnectionKey
            ) || null

            if (!this.selectedConnection) {
                this.setSelectedConnection(null)
            } else {
                this.selectedConnectionField = this.selectedConnection.fromRow.data
                const point = this.selectedConnection.midpoint()
                this.selectedConnectionPosition = {
                    x: this.origin.x + point.x * this.zoom.scale,
                    y: this.origin.y + point.y * this.zoom.scale,
                }
            }
        }

        if (this.element) {
            this.element.style.cursor = this.connectionInteraction
                ? 'grabbing'
                : hoveredConnection ? 'grab' : ''
        }
    }

    beginConnectionInteraction(connection, screenX, screenY) {
        if (!connection) return false

        this.setSelectedConnection(connection)
        this.connectionInteraction = {
            key: this.connectionKey(connection),
            field: connection.fromRow.data,
            table: connection.fromRow.table.data,
            originalRoute: cloneConnectionRoute(connection.fromRow.data.connection_route),
            startPoint: this.screenToWorld(screenX, screenY),
            waypointIndex: null,
            moved: false,
        }
        return true
    }

    updateConnectionInteraction(screenX, screenY) {
        const interaction = this.connectionInteraction
        if (!interaction) return false

        const point = this.screenToWorld(screenX, screenY)
        if (!interaction.moved) {
            const movement = Math.hypot(
                point.x - interaction.startPoint.x,
                point.y - interaction.startPoint.y
            )
            if (movement < 3 / this.zoom.scale) return true

            const connection = this.renderedConnections.find(item =>
                this.connectionKey(item) === interaction.key
            ) || this.selectedConnection
            if (!connection) return true

            const route = cloneConnectionRoute(interaction.originalRoute) || {
                fromSide: connection.fromSide,
                toSide: connection.toSide,
                waypoints: [],
            }
            const tolerance = 9 / this.zoom.scale
            let waypointIndex = connection.waypointIndexAt(interaction.startPoint, tolerance)
            if (waypointIndex === -1) {
                waypointIndex = connection.waypointInsertionIndex(interaction.startPoint)
                route.waypoints.splice(waypointIndex, 0, { ...interaction.startPoint })
            }

            interaction.waypointIndex = waypointIndex
            interaction.moved = true
            interaction.field.connection_route = route
        }

        interaction.field.connection_route.waypoints[interaction.waypointIndex] = point
        return true
    }

    async endConnectionInteraction() {
        const interaction = this.connectionInteraction
        if (!interaction) return false
        this.connectionInteraction = null

        if (!interaction.moved) return true

        const route = cloneConnectionRoute(interaction.field.connection_route)
        if (interaction.originalRoute) {
            interaction.field.connection_route = interaction.originalRoute
        } else {
            delete interaction.field.connection_route
        }

        if (!await this.canEditConnectionRoutes()) return false

        const command = new UpdateConnectionRouteCommand(this.tab, {
            table: interaction.table,
            field: interaction.field,
            route,
        })
        await command.execute()
        return true
    }

    async updateSelectedConnectionSide(endpoint, side) {
        if (!await this.canEditConnectionRoutes()) return false

        const field = this.selectedConnectionField
        const connection = this.selectedConnection
        const table = this.sourceTableForField(field)
        if (!field || !connection || !table) return false

        const route = cloneConnectionRoute(field.connection_route) || { waypoints: [] }
        const key = endpoint === 'from' ? 'fromSide' : 'toSide'
        if (side === 'left' || side === 'right') {
            route[key] = side
        } else {
            delete route[key]
        }

        const command = new UpdateConnectionRouteCommand(this.tab, {
            table,
            field,
            route: normalizeConnectionRoute(route),
        })
        await command.execute()
        return true
    }

    async resetSelectedConnectionRoute() {
        if (!await this.canEditConnectionRoutes()) return false

        const field = this.selectedConnectionField
        const table = this.sourceTableForField(field)
        if (!field || !table) return false

        const command = new UpdateConnectionRouteCommand(this.tab, {
            table,
            field,
            route: null,
        })
        await command.execute()
        return true
    }

    get isBiggerThanViewPort() {
        return this.width < this.maxX - this.minX || this.height < this.maxY - this.minY
    }

    get isTranslated() {
        const offset = 100
        const {x, y} = this.origin
        return x < -offset || x > offset || y < -offset || y > offset
    }

    get hasOffscreenItems() {
        return !this.isPointVisible(this.minX, this.minY)
            || !this.isPointVisible(this.minX, this.maxY)
            || !this.isPointVisible(this.maxX, this.minY)
            || !this.isPointVisible(this.minX, this.maxY)
    }

    draw(tab) {
        if (this.exporting) return
        // set defaults
        this.ctx.textBaseline = 'top'
        this.ctx.font = canvasFont

        this.ctx.save()
        this.clear()

        this.ctx.translate(this.origin.x, this.origin.y)

        this.ctx.scale(this.zoom.scale, this.zoom.scale)

        let minX = null
        let minY = null
        let maxX = null
        let maxY = null

        const scene = this.createRenderScene(tab)
        for (const item of scene.items) {
            item.draw()

            // update canvas points
            if (minX === null || item.left < minX) minX = item.left
            if (maxX === null || item.right > maxX) maxX = item.right
            if (minY === null || item.top < minY) minY = item.top
            if (maxY === null || item.bottom > maxY) maxY = item.bottom

        }

        this.ctx.globalCompositeOperation = 'destination-over'
        this.drawSceneConnections(scene, tab)
        this.ctx.globalCompositeOperation = 'source-over'

        this.drawMouseConnector()

        if (minX !== null) this.minX = minX
        if (maxX !== null) this.maxX = maxX
        if (minY !== null) this.minY = minY
        if (maxY !== null) this.maxY = maxY

        this.ctx.restore()
    }

    createRenderScene(tab) {
        const dataItems = tab.schema.schema_data.tables.concat(tab.schema.schema_data.notes)
        const items = []
        let minX = null
        let minY = null
        let maxX = null
        let maxY = null

        for (const itemData of dataItems) {
            if (itemData.checked === false) continue
            if (itemData.resource === 'note' && !itemData.content) continue

            const item = itemData.fields ? new Table(this, itemData) : new Note(this, itemData)
            items.push(item)

            if (minX === null || item.left < minX) minX = item.left
            if (maxX === null || item.right > maxX) maxX = item.right
            if (minY === null || item.top < minY) minY = item.top
            if (maxY === null || item.bottom > maxY) maxY = item.bottom
        }

        const tables = items.filter(item => Array.isArray(item.rows))
        return {
            items,
            resolver: new RelationshipResolver(tables),
            minX,
            minY,
            maxX,
            maxY
        }
    }

    drawSceneConnections(scene, tab) {
        const showPhysical = tab.settings.view === 'detailed_view'
            || tab.settings.view === 'physical_relationships_view'
        const physicalConnections = []
        const logicalConnections = []

        for (const item of scene.items) {
            if (!Array.isArray(item.rows)) continue

            if (showPhysical) {
                for (const row of item.rows) {
                    if (!row.hasConnection) continue
                    const toRow = scene.resolver.resolveRow(row)
                    if (!toRow) continue

                    const connection = new Connection(this, row)
                    connection.toRow = toRow
                    physicalConnections.push(connection)
                }
            }

            for (const logicalConnection of item.logicalConnections) {
                const toItem = scene.resolver.resolveTable(logicalConnection.relation)
                if (!toItem) continue

                logicalConnection.toItem = toItem
                logicalConnections.push(logicalConnection)
            }
        }

        assignConnectionLanes(physicalConnections)
        physicalConnections.forEach(connection => connection.draw())
        logicalConnections.forEach(connection => connection.draw())

        if (!this.exporting) {
            this.renderedConnections = physicalConnections
            this.updateConnectionInteractionState(physicalConnections)
        }
    }

    /**
     * Render the schema to an arbitrary Canvas 2D-compatible context (e.g. jsPDF's
     * doc.context2d) for vector export. Uses a two-pass painter's order
     * (connections first, then items on top) instead of relying on
     * globalCompositeOperation, which most PDF ctx shims do not implement.
     *
     * Runs synchronously so the live rAF loop cannot interleave.
     */
    drawForExport(exportCtx, tab) {
        const savedCtx = this.ctx
        const savedZoom = this.zoom.scale
        const savedOriginX = this.origin.x
        const savedOriginY = this.origin.y

        this.exporting = true
        this.ctx = exportCtx
        this.zoom.scale = 1
        this.origin.x = 0
        this.origin.y = 0

        try {
            exportCtx.textBaseline = 'top'
            exportCtx.font = canvasFont

            const scene = this.createRenderScene(tab)
            const { minX, minY, maxX, maxY } = scene

            if (minX !== null) {
                this.minX = minX; this.maxX = maxX
                this.minY = minY; this.maxY = maxY
            }

            // Pass 1: connections (drawn first = end up beneath items)
            this.drawSceneConnections(scene, tab)

            // Pass 2: items on top
            for (const item of scene.items) {
                item.draw()
            }

            return { minX, minY, maxX, maxY }
        } finally {
            this.ctx = savedCtx
            this.zoom.scale = savedZoom
            this.origin.x = savedOriginX
            this.origin.y = savedOriginY
            this.exporting = false
        }
    }

    async locateItem(item, highlightDuration = 900) {
        let cx = (this.width / this.zoom.scale) / 2
        let cy = (this.height / this.zoom.scale) / 2
        await this.updateOrigin((-item.left + cx - 100) * this.zoom.scale, (-item.top + cy - 100) * this.zoom.scale, async () => {
            this.highlightedItem = item
            await _sleep(highlightDuration)
            this.highlightedItem = null
        })
    }

    get vpRight() {
        return (-this.origin.x + this.width) / this.zoom.scale
    }

    get vpBottom() {
        return (-this.origin.y + this.height) / this.zoom.scale
    }

    isPointVisible(x, y) {
        return x * this.zoom.scale > -this.origin.x && x < this.vpRight
            && y * this.zoom.scale > -this.origin.y && y < this.vpBottom
    }

    initialize(element) {
        this.element = element
        this.ctx = element.getContext('2d')
        this.updateCanvasSize()
        this.mouse.initialize(this)
    }

    clampZoom(scale) {
        return Math.min(this.zoom.max, Math.max(this.zoom.min, scale))
    }

    getZoomAnchorOrigin(targetScale, anchorX = this.width / 2, anchorY = this.height / 2) {
        const worldX = (anchorX - this.origin.x) / this.zoom.scale
        const worldY = (anchorY - this.origin.y) / this.zoom.scale

        return {
            x: anchorX - worldX * targetScale,
            y: anchorY - worldY * targetScale
        }
    }

    getNextZoomScale(direction) {
        const multiplier = 1 + this.zoom.delta
        const currentScale = this.zoom.scale
        const nextScale = direction > 0 ? currentScale * multiplier : currentScale / multiplier
        return this.clampZoom(nextScale)
    }

    setZoom(scale, options = {}) {
        const {
            anchorX = this.width / 2,
            anchorY = this.height / 2,
            duration = 500,
            adjustOrigin = true
        } = options

        const targetScale = this.clampZoom(scale)
        if (adjustOrigin) {
            const nextOrigin = this.getZoomAnchorOrigin(targetScale, anchorX, anchorY)
            this.updateOrigin(nextOrigin.x, nextOrigin.y, () => {
            }, duration)
        }

        return new Tween(this.zoom)
            .to({scale: targetScale}, duration)
            .easing(Easing.Cubic.Out)
            .onComplete(() => {
            })
            .start()
    }

    zoomIn(options = {}) {
        if (this.zoom.scale < this.zoom.max) {
            this.setZoom(this.getNextZoomScale(1), options)
        }
    }

    zoomOut(options = {}) {
        if (this.zoom.scale > this.zoom.min) {
            this.setZoom(this.getNextZoomScale(-1), options)
        }
    }

    resetZoom(options = {}) {
        const normalizedOptions = typeof options === 'boolean'
            ? { duration: options ? 500 : 0 }
            : options
        const duration = normalizedOptions.duration ?? 500
        const center = calculateViewportCenter(this, 1)

        if (duration === 0) {
            this.updateOrigin(center.x, center.y)
        } else {
            this.updateOrigin(center.x, center.y, () => {
            }, duration)
        }

        return this.setZoom(1, {
            ...normalizedOptions,
            adjustOrigin: false,
        })
    }

    resetZoomAndCenter() {
        return this.resetZoom()
    }

    clear() {
        let w = Math.max(parseInt(this.maxX), parseInt(this.width))
        let h = Math.max(parseInt(this.maxY), parseInt(this.height))
        this.ctx.clearRect(0, 0, w, h)
    }

    updateCanvasSize(w, h) {
        if (w) this.width = w
        if (h) this.height = h

        const dpr = window.devicePixelRatio

        this.element.width = this.width * dpr
        this.element.height = this.height * dpr

        this.ctx.scale(dpr, dpr)

        this.element.style.width = `${this.width}px`
        this.element.style.height = `${this.height}px`

        this.onSizeUpdate()
    }

    updateOrigin(x, y, onComplete, duration = 500) {
        if (!onComplete) {
            this.origin.x = x
            this.origin.y = y
            this.onOriginUpdated(this.origin)
            return
        }

        return new Tween(this.origin)
            .to({x, y}, duration)
            .easing(Easing.Cubic.Out)
            .onComplete(onComplete)
            .start()

    }

    // events
    onDragEnd() {
        const settings = useSettingsStore()
        if (settings.settings.lockDragging) {
            this.setDraggedObject(null)
        }
        if (this.draggedObject) {
            let selectedItems = this.selectedItems?.items || []
            if (selectedItems.length) {
                // following line handles the scenario when a user selects some items but the drags an item that isn't selected using shift key
                if (!selectedItems.includes(this.draggedObject)) selectedItems.push(this.draggedObject)

                let command = new MoveMultipleObjectsCommand(this.tab, {
                    objs: selectedItems,
                    left: this.mouse.dx / this.zoom.scale,
                    top: this.mouse.dy / this.zoom.scale
                })
                command.execute()
            } else {
                let command = new MoveObjectCommand(this.tab, {
                    obj: this.draggedObject,
                    left: this.mouse.dx / this.zoom.scale,
                    top: this.mouse.dy / this.zoom.scale
                })
                command.execute()
            }

            this.setDraggedObject(null)
        }
    }

    onOriginUpdated() {
    }

    onSizeUpdate() {
    }

    onSelect(item) {
        this.setSelectedConnection(null)
        if (!item) {
            this.setSelectedObject(null)
            this.selectedItems.clear()
            return
        }
        this.setSelectedObject(item)
    }

    onHover(item) {
    }

    onDrag(item) {
    }

    drawMouseConnector() {
        // draw logical relationship connector when only 1 table is selected
        if (!this.selectedLogicalTable)
            return
        let mouseX = (-this.origin.x + this.mouse.x) / this.zoom.scale
        let mouseY = (-this.origin.y + this.mouse.y) / this.zoom.scale

        let tableX = this.selectedLogicalTable.cx
        let tableY = this.selectedLogicalTable.cy
        let direction = ['right', 'left']
        const directions = {left: -1, right: 1}

        if (this.selectedLogicalTable.cx > mouseX) {
            tableX = this.selectedLogicalTable.left
            direction = ['left', 'right']
        } else if (this.selectedLogicalTable.cx < mouseX) {
            tableX = this.selectedLogicalTable.right
        }

        if (this.selectedLogicalTable.cy < mouseY) {
            tableY = this.selectedLogicalTable.bottom
        } else if (this.selectedLogicalTable.cy > mouseY) {
            tableY = this.selectedLogicalTable.top
        }

        let from = direction[0]
        let to = direction[1]

        this.ctx.globalCompositeOperation = 'destination-over'
        this.ctx.beginPath();
        this.ctx.strokeStyle = cssVar('--warning')
        this.ctx.lineWidth = 3
        this.ctx.setLineDash([6]);
        const lineType = this.settings?.lineType || LINE_TYPES.BEZIER
        const offset = lineType === LINE_TYPES.FLOWCHART ? 30 : (lineType === LINE_TYPES.STRAIGHT ? 20 : 100)

        let fromX = tableX
        let fromY = tableY
        let toX = mouseX
        let toY = mouseY

        let cp1x = fromX + offset * directions[from]
        let cp1y = fromY
        let cp2x = toX + offset * directions[to]
        let cp2y = toY

        this.ctx.moveTo(fromX, fromY);


        if (lineType === LINE_TYPES.FLOWCHART) {
            const cpx = Math.max(cp1x, cp2x)
            this.ctx.lineTo(cpx, cp1y)
            this.ctx.lineTo(cpx, cp2y)
            this.ctx.lineTo(toX, toY)
        } else if (lineType === LINE_TYPES.STRAIGHT) {
            this.ctx.lineTo(cp1x, cp1y)
            this.ctx.lineTo(cp2x, cp2y)
            this.ctx.lineTo(toX, toY)
        } else {
            this.ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, toX, toY)
        }
        this.ctx.stroke();
    }

    getMax(a, b, key) {
        let item1 = new Table(this, a)
        let item2 = new Note(this, b)
        return Math.max(item1[key], item2[key])
    }

    newItemPosition(type = 'table') {
        const gap = 40
        let top = gap
        let left = gap

        let lastItem = type === 'note' ? this.tab.schema.schema_data.notes[this.tab.schema.schema_data.notes.length - 1] : this.tab.schema.schema_data.tables[this.tab.schema.schema_data.tables.length - 1]

        if (lastItem) {
            lastItem = type === 'note' ? new Note(this, lastItem) : new Table(this, lastItem)

            let lastTable = this.tab.schema.schema_data.tables?.length ? new Table(this, this.tab.schema.schema_data.tables[this.tab.schema.schema_data.tables.length - 1]) : {}
            let lastNote = this.tab.schema.schema_data.notes?.length ? new Note(this, this.tab.schema.schema_data.notes[this.tab.schema.schema_data.notes.length - 1]) : {}

            top = Math.max(lastTable.top || 0, lastNote.top || 0, lastItem.top || 0)
            left = Math.max(lastTable.right || 0, lastNote.right || 0, lastItem.right || 0) + gap


            if (left > this.width - 200) {
                top = Math.max(lastTable.bottom || 0, lastNote.bottom || 0, lastItem.bottom || 0) + gap
                left = gap
            }
        }
        return {top, left}
    }


    getItemPositionsForTables(tables) {
        const gap = 40

        let lastPositions = this.newItemPosition('table')

        let data = []

        for (const table of tables) {
            if (table.top !== null && table.left !== null) continue
            table.top = lastPositions.top
            table.left = lastPositions.left

            let tableObj = new Table(this, table)

            let top = Math.max(tableObj.top || 0)
            let left = Math.max(tableObj.right || 0) + gap

            if (left > this.width - tableObj.width) {
                top = Math.max(tableObj.bottom || 0) + gap * 2
                left = gap
            }

            lastPositions.top = top
            lastPositions.left = left

            data.push({
                p_id: table.p_id,
                resource: table.resource,
                top: tableObj.top,
                left: tableObj.left
            })
        }
        return data
    }

    get sizeX() {
        return this.maxX - this.minX
    }

    get sizeY() {
        return this.maxY - this.minY
    }

    fitToScreen(options = {}) {
        const fit = calculateViewportFit(this, options)
        this.updateOrigin(fit.x, fit.y, d => {
        })
        this.setZoom(fit.zoom, { adjustOrigin: false })
    }

    fitWidth() {
        const fit = calculateViewportFit(this, { fitHeight: false })
        this.updateOrigin(fit.x, fit.y, d => {
        })
        this.setZoom(fit.zoom, { adjustOrigin: false })
    }
}
